/* eslint-disable @typescript-eslint/no-explicit-any */
// JSON API deserialiser // TODO: why two?
import {Platform} from 'react-native';
import {AxiosInstance, InternalAxiosRequestConfig} from 'axios';
import * as deserializeJsonApi from 'deserialize-json-api';
import Jsona from 'jsona';

import {config as appConfig} from '../config';
import {logger, serializeError} from '../utils/logger';

import {getInterceptorCallbacks} from './interceptorCallbacks';

const jsonaDeserialiser = new Jsona();

/**
 * Extract product_ids from a raw JSON:API order response.
 *
 * MFL /ecommerce/order omits extra_prices + stickers on order-lines; cartLines()
 * fetches them from /ecommerce/search using these ids.
 *
 * Walks both `data` (when the order-lines are the primary resources, e.g. some
 * coupon-validation payloads) and `included` (standard order responses). Same
 * extractor handles /ecommerce/order, /ecommerce/coupon, /ecommerce/order-validate
 * — the historical `data.type === 'coupon-validations'` special-case is no longer
 * needed because we don't depend on the root resource type, only on the presence
 * of `order-lines` entries anywhere in the document.
 */
export function extractOrderLineProductIds(response: any): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();

  const collect = (entry: any) => {
    if (entry?.type !== 'order-lines') return;
    const pid = entry?.attributes?.product_id;
    if (typeof pid !== 'string' || pid.length === 0) return;
    if (seen.has(pid)) return;
    seen.add(pid);
    ids.push(pid);
  };

  const data = response?.data?.data;
  if (Array.isArray(data)) data.forEach(collect);
  else if (data) collect(data);

  const included = response?.data?.included;
  if (Array.isArray(included)) included.forEach(collect);

  return ids;
}

/**
 * Fetch product data (extra_prices, stickers) for every order-line in the
 * cart response. /ecommerce/search is the product catalog endpoint, so we
 * query it by `product_ids` (the only filter that works) instead of the
 * legacy `order_id` parameter which the catalog endpoint ignores and which
 * returns an empty array.
 */
async function cartLines(response: any, axiosInstance: AxiosInstance) {
  const productIds = extractOrderLineProductIds(response);
  if (productIds.length === 0) return [];

  try {
    const lines = await axiosInstance.get('/ecommerce/search', {
      params: {product_ids: productIds},
    });
    return lines || [];
  } catch (err) {
    logger.debug('APIInterceptor', 'Error searching lines', {
      productIdCount: productIds.length,
      ...serializeError(err),
    });
    return [];
  }
}

/**
 * Walk every order_lines array in the deserialised cart response and copy
 * extra_prices + stickers from the matching augment line (matched by product_id).
 * Defensive: skips silently when shapes don't match.
 */
export function hoistAugmentedFieldsOntoOrderLines(result: any, augmentLines: any[]): void {
  if (!Array.isArray(augmentLines) || augmentLines.length === 0) return;

  const byProductId = new Map<string, any>();
  for (const line of augmentLines) {
    const pid = line?.product_id ?? line?.attributes?.product_id;
    if (pid) byProductId.set(pid, line);
  }
  if (byProductId.size === 0) {
    // Augment fetch returned rows but none carried product_id — shape drift in
    // /ecommerce/search response (e.g. unexpected JSON:API wrapper, renamed field).
    // Log so we notice rather than silently dropping member pricing / stickers.
    logger.debug(
      'APIInterceptor',
      'hoistAugmentedFieldsOntoOrderLines: augment lines have no product_id; possible shape drift',
      {augmentCount: augmentLines.length, sampleKeys: Object.keys(augmentLines[0] ?? {})},
    );
    return;
  }

  const orders = Array.isArray(result?.data) ? result.data : result?.data ? [result.data] : [];
  for (const order of orders) {
    const orderLines = order?.order_lines;
    if (!Array.isArray(orderLines)) continue;
    for (const ol of orderLines) {
      const match = byProductId.get(ol?.product_id);
      if (!match) continue;
      if (ol.extra_prices === undefined && match.extra_prices !== undefined) {
        ol.extra_prices = match.extra_prices;
      }
      if ((!ol.stickers || ol.stickers.length === 0) && Array.isArray(match.stickers)) {
        ol.stickers = match.stickers;
      }
    }
  }
}

export const jsonApiDeserialisationResponseInterceptor =
  (axiosInstance: AxiosInstance) => async (response: any) => {
    const url = response.config.url;

    if (!url) return response;

    // V2 and V3 endpoints return standard JSON, not JSON:API format - skip deserialization
    if (url.includes('/v2/') || url.includes('/v3/')) {
      return response;
    }

    if (url.startsWith('/ecommerce') || url.startsWith('/support')) {
      // TODO: maybe just move JSONApi deserialisation to the middleware?
      if (
        [
          '/ecommerce/order',
          '/ecommerce/order/recent',
          '/ecommerce/delivery-details',
          '/ecommerce/order-note',
          '/ecommerce/coupon',
          '/ecommerce/order-validate',
          '/ecommerce/order-checkout',
        ].includes(url) ||
        url.match(/\/ecommerce\/orders\/[a-z0-9]{24}/)
      ) {
        // MFL /ecommerce/order response omits extra_prices + stickers on order-lines.
        // cartLines() fetches the same products via /ecommerce/search?product_ids=...
        // and hoistAugmentedFieldsOntoOrderLines() copies extra_prices + stickers
        // onto each deserialised order_line by matching on product_id.
        const augmentLines = (await cartLines(response, axiosInstance)) as any[];

        const result = deserializeJsonApi.deserialize(response.data);
        hoistAugmentedFieldsOntoOrderLines(result, augmentLines);

        return result;
      } else if (url === '/ecommerce/departments/lines') {
        // Need to include metadata to enable pagination.
        const meta = response.data.meta;
        return {data: jsonaDeserialiser.deserialize(response.data), meta};
      }

      // Infer JSON API deserialisation with presence of response.data.data - https://jsonapi.org/format/#document-top-level
      if (response.data.data) {
        const deserialisedResponseData = jsonaDeserialiser.deserialize(response.data);
        if (!deserialisedResponseData) {
          logger.error(
            'APIInterceptor',
            "Deserialised 'response.data' is empty, double check whether data is JSON API formatted",
            {responseData: response.data},
          );
          return response;
        }
        return deserialisedResponseData;
      }

      return response;
    }

    if (url === '/catalogues/catalogueHtml' || url === '/3ds/browserDataJs') {
      return response.data;
    }

    return response;
  };

/**
 * Sets context headers on every outgoing request.
 *
 * - deviceId: always set when available (required by middleware for all endpoints)
 * - x-session-id: native only — web relies on mw_session httpOnly cookie (withCredentials:true)
 *
 * Replaces mflAuthHeadersRequestInterceptor (DIS-1026).
 * No route filter — deviceId is needed on public endpoints too.
 */
export function contextHeadersRequestInterceptor(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const callbacks = getInterceptorCallbacks();

  const deviceId = callbacks.getDeviceId?.();
  if (deviceId) {
    config.headers.set('deviceId', deviceId);
  }

  // REQ-5 (DIS-1384): test-build-only OTP bypass. Double-gated — the value is
  // env-baked ONLY in dev/automation profiles (empty in beta/prod), AND we
  // require ENV==='DEV' at runtime, so a production build can never send it.
  if (appConfig.ENV === 'DEV' && appConfig.AUTOMATION_TEST_ID) {
    config.headers.set('automation-test-id', appConfig.AUTOMATION_TEST_ID);
  }

  // Native only: send server session ID as request header.
  // Web sends the mw_session httpOnly cookie automatically via withCredentials:true.
  if (Platform.OS !== 'web') {
    const sessionId = callbacks.getServerSessionId?.();
    if (sessionId) {
      config.headers.set('x-session-id', sessionId);
    }
  }

  return config;
}
